import { CheckboxWithLabel } from '@adotel/components'
import { useMutation } from '@apollo/client'
import { Button, Chip, Typography } from '@material-ui/core'
import RestoreIcon from '@material-ui/icons/Restore'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import SectionTitle from '../../components/SectionTitle'
import TimePeriodForm from '../../components/TimePeriodForm'
import { UNLOCK_USER_MURATION } from '../../graphql/userManagement'
import {
  addDaysToDate,
  DateObject,
  getTodayDate,
  gregorianToJalaali
} from '../../helpers/date'
import { getTranslate } from '../../localization'
import { UserForCreateAndModify } from '../../types/users'
import useStyles from './styles/userSecurity.styles'

/**
 * Carries BOTH calendar representations, because TimePeriodForm renders `jalaaliStartDate`
 * in Persian and `gregorianStartDate` in English. The old seed set only the Gregorian
 * field, so in Persian the control rendered `undefined` and painted an empty bar while the
 * calendar underneath already had a day selected — reported as "Active Until does not show
 * the active period".
 *
 * Same shape as AccessManagement/ChannelFilters, which was already doing this. Module-level
 * because it closes over nothing in the component.
 */
const buildDateObject = (gregorian: string): DateObject => ({
  gregorianStartDate: gregorian,
  jalaaliStartDate: gregorianToJalaali(gregorian)
})

interface UserAccessProps {
  values: UserForCreateAndModify
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<UserForCreateAndModify>
  touched: FormikTouched<UserForCreateAndModify>
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  isSubmitting: boolean
}

const UserSecurity: React.FC<UserAccessProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting
}) => {
  const classes = useStyles()
  const params = useParams<{ id: string }>()
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const [timeSelected, setTimeSelected] = useState<boolean>(false)
  const [dateObject, setDateObject] = useState<DateObject>(() =>
    buildDateObject(addDaysToDate(getTodayDate(), 30))
  )

  /*
   * Whoever sets Active Until first owns it — the stored value on load, or the operator.
   *
   * Without this, the sync below is a clobber: USER_QUERY resolves late, so an operator who
   * opens the picker and picks a date BEFORE the response lands has that choice silently
   * replaced by the stored one, with nothing on screen to say it happened.
   *
   * The equality guard alone does not prevent that — it compares VALUES, so two genuinely
   * different dates pass straight through. It stops the two effects ping-ponging; it says
   * nothing about who is entitled to write.
   */
  const activeUntilClaimed = useRef(false)

  /*
   * Show the stored date when editing an existing user.
   *
   * `values.activeUntil` arrives from USER_QUERY's onCompleted, which resolves AFTER this
   * component mounts, so the useState initialiser above cannot see it — and nothing else
   * copied it in. The effect below only ever wrote in the other direction, so an existing
   * user's date was never displayed; every user looked like a new one defaulted to
   * today + 30.
   */
  useEffect(() => {
    if (activeUntilClaimed.current || !values.activeUntil) return

    activeUntilClaimed.current = true
    setDateObject((current) =>
      current.gregorianStartDate === values.activeUntil
        ? current
        : buildDateObject(values.activeUntil)
    )
  }, [values.activeUntil])

  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timeSelected
      ) {
        setTimeSelected(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timeSelected])

  useEffect(() => {
    if (dateObject && dateObject.gregorianStartDate) {
      setFieldValue('activeUntil', dateObject.gregorianStartDate)
    }
  }, [dateObject])

  const [unlock] = useMutation(UNLOCK_USER_MURATION)
  return (
    <div>
      <SectionTitle title={getTranslate('Status & Security')} marginBottom />
      <div className={classes.firstRow}>
        <div ref={datePickerRef} className={classes.datePicker}>
          <Typography>{getTranslate('Active Until')}</Typography>
          <TimePeriodForm
            range={false}
            isOpen={timeSelected}
            setTimePickerSelected={setTimeSelected}
            dateObject={dateObject}
            onChange={(data: DateObject) => {
              // The operator has now chosen a date, so a late-arriving stored value must
              // not replace it. See activeUntilClaimed.
              activeUntilClaimed.current = true
              setDateObject(data)
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            enableAllDays
            openAtToday={!params.id}
            left
          />
        </div>
        <CheckboxWithLabel
          name="active"
          placeholder={getTranslate('Is active')}
          value={values.active}
          checked={values.active}
          onChange={(event) =>
            setFieldValue('active', event.currentTarget.checked)
          }
          error={(touched.active && !!errors.active) || undefined}
          helpertext={
            touched.active ? getTranslate(errors.active || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <CheckboxWithLabel
          name="active"
          placeholder={getTranslate('Two Factor Authentication')}
          value={values.twoFactorEnabled}
          checked={values.twoFactorEnabled}
          onChange={(event) =>
            setFieldValue('twoFactorEnabled', event.currentTarget.checked)
          }
          error={
            (touched.twoFactorEnabled && !!errors.twoFactorEnabled) || undefined
          }
          helpertext={
            touched.twoFactorEnabled
              ? getTranslate(errors.twoFactorEnabled || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        {values.lastLogin && (
          <div className={classes.row}>
            <RestoreIcon className={classes.icon} />
            <Typography className={classes.boldText}>
              {getTranslate('Last login')}:
            </Typography>
            <Typography className={classes.lightText}>
              {values.lastLogin}
            </Typography>
          </div>
        )}
      </div>
      {!!params.id && (
        <div className={classes.horizontal}>
          <Typography>{getTranslate('Login Status')}</Typography>
          <Chip
            size="small"
            label={
              <Typography variant="h5" className={classes.chipText}>
                {` ${values.locked ? 'Locked' : 'Normal'}`}
              </Typography>
            }
            className={classes.paymentChip}
            variant="default"
          />
          {!values.locked && (
            <Typography className={classes.chipText}>
              {getTranslate('No locked detected')}
            </Typography>
          )}
          <Button
            variant="text"
            disabled={!values.locked}
            onClick={() => {
              unlock({ variables: { userID: params.id } })
            }}>
            <Typography variant="h5" className={classes.btnText}>
              {getTranslate('Unlock')}
            </Typography>
          </Button>
        </div>
      )}
    </div>
  )
}
export default UserSecurity
